import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Stack } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { getAllState, getAllBarberShop, postShopCreate, postShopCreatePublic } from "../../Service";
import { HtmlTagValidation, BlockSQLInjection, checkDuplicateShop, DUPLICATE_SHOP_MESSAGE, DUPLICATE_SHOP_HELPER_MESSAGE, buildShopCreatePayload } from "./index";

// Add-a-business page, reached from BusinessSelectionWizard's "Add" button.
// Reuses the same fields/API as Admin > Business List's Add Shop. Navigates back to the
// page it came from once the shop is created, carrying the new shop forward via location state.
// Used both right after registration (no session yet -> guest call) and after login
// (session already exists -> authenticated call, since the backend requires it once logged in).
export default function AddShopWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const stylistToken = useSelector((store) => store.user["stylisttoken"]);
  const { clientId, firstName: professionalFirstName, lastName: professionalLastName, phoneNumber: professionalPhoneNumber } = location.state || {};
  const parentPath = location.pathname.replace(/\/add-shop$/, "");
  const createShop = stylistToken ? postShopCreate : postShopCreatePublic;

  const [stateProvinceOption, setStateProvinceOption] = useState([]);

  useEffect(() => {
    getAllState().then((res) => {
      if (res.data.statusCode === 200) {
        const options = res.data.stateList.map((s) => ({ label: s.stateName, value: s.stateId }));
        setStateProvinceOption(options);
      }
    });
  }, []);

  const initialAddShopValues = {
    createdBy: clientId || 0,
    name: "",
    address1: "",
    address2: "",
    city: "",
    zipCode: "",
    phoneNumber: "",
    isOwnership: "",
    managerFirstName: "",
    managerLastName: "",
    managerPhoneNumber: "",
    active: true,
    countryId: 1,
    stateProvinceId: "",
  };
  const [addShopFormValues, setAddShopFormValues] = useState(initialAddShopValues);
  const [addShopFormErrors, setAddShopFormErrors] = useState({});
  const [addShopLoader, setAddShopLoader] = useState(false);

  const formatPhoneDigits = (digits) => {
    if (!digits || digits.length === 0) return "";
    let fmt = "(" + digits.slice(0, 3);
    if (digits.length > 3) fmt += ") " + digits.slice(3, 6);
    if (digits.length > 6) fmt += "-" + digits.slice(6, 10);
    return fmt;
  };

  const handleAddShopPhoneKeyDown = (e) => {
    BlockSQLInjection(e);
    if (e.key === "Backspace" && ["phoneNumber", "managerPhoneNumber"].includes(e.target.name)) {
      e.preventDefault();
      const digits = (addShopFormValues[e.target.name] || "").replace(/\D/g, "");
      setAddShopFormValues((prev) => ({ ...prev, [e.target.name]: formatPhoneDigits(digits.slice(0, -1)) }));
    }
  };

  const handleAddShopFormChange = (e) => {
    const { name, value } = e.target;

    if (name === "isOwnership") {
      // Owner = Yes: capture the professional's own details as the manager, instead of asking for them
      setAddShopFormValues((prev) => ({
        ...prev,
        isOwnership: value,
        managerFirstName: value === "Y" ? (professionalFirstName || "") : "",
        managerLastName: value === "Y" ? (professionalLastName || "") : "",
        managerPhoneNumber: value === "Y" ? (professionalPhoneNumber || "") : "",
      }));
      return;
    }

    let filteredValue = value;

    if (["managerFirstName", "managerLastName", "city"].includes(name)) {
      filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
    } else if (["phoneNumber", "managerPhoneNumber"].includes(name)) {
      const digits = value.replace(/\D/g, "").slice(0, 10);
      filteredValue = formatPhoneDigits(digits);
    } else if (name === "zipCode") {
      filteredValue = value.replace(/[^0-9]/g, "").slice(0, 5);
    }

    const capitalizedFields = ["name", "address1", "address2", "city", "managerFirstName", "managerLastName"];
    const finalValue = capitalizedFields.includes(name) && filteredValue.length > 0
      ? filteredValue.charAt(0).toUpperCase() + filteredValue.slice(1)
      : filteredValue;
    setAddShopFormValues((prev) => ({ ...prev, [name]: finalValue }));
  };

  const validateAddShop = (values) => {
    const errors = {};

    if (!values.name || values.name.trim() === "") {
      errors.name = "Business name is required";
    } else if (!HtmlTagValidation(values.name)) {
      errors.name = "Invalid input";
    }

    if (!values.address1 || values.address1.trim() === "") {
      errors.address1 = "Address 1 is required";
    } else if (!HtmlTagValidation(values.address1)) {
      errors.address1 = "Invalid input";
    }

    if (!values.city || values.city.trim() === "") {
      errors.city = "City is required";
    } else if (!/^[a-zA-Z\s]+$/.test(values.city.trim())) {
      errors.city = "City should contain letters only";
    } else if (!HtmlTagValidation(values.city)) {
      errors.city = "Invalid input";
    }

    if (!values.stateProvinceId) {
      errors.stateProvinceId = "Please select a state";
    }

    if (!values.zipCode || values.zipCode.trim() === "") {
      errors.zipCode = "Zipcode is required";
    } else if (values.zipCode.length !== 5) {
      errors.zipCode = "Zipcode must be 5 digits";
    }

    if (!values.phoneNumber || values.phoneNumber.trim() === "") {
      errors.phoneNumber = "Phone number is required";
    } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(values.phoneNumber.trim())) {
      errors.phoneNumber = "Phone number must be 10 digits";
    }

    if (!values.isOwnership || values.isOwnership === "") {
      errors.isOwnership = "Please select ownership";
    }

    if (values.isOwnership === "Y") {
      if (!values.managerFirstName || values.managerFirstName.trim() === "") {
        errors.managerFirstName = "First name is required";
      } else if (!/^[a-zA-Z\s]+$/.test(values.managerFirstName.trim())) {
        errors.managerFirstName = "First name should contain letters only";
      } else if (!HtmlTagValidation(values.managerFirstName)) {
        errors.managerFirstName = "Invalid input";
      }
      if (!values.managerLastName || values.managerLastName.trim() === "") {
        errors.managerLastName = "Last name is required";
      } else if (!/^[a-zA-Z\s]+$/.test(values.managerLastName.trim())) {
        errors.managerLastName = "Last name should contain letters only";
      } else if (!HtmlTagValidation(values.managerLastName)) {
        errors.managerLastName = "Invalid input";
      }
      if (!values.managerPhoneNumber || values.managerPhoneNumber.trim() === "") {
        errors.managerPhoneNumber = "Phone number is required";
      } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(values.managerPhoneNumber.trim())) {
        errors.managerPhoneNumber = "Phone number must be 10 digits";
      }
    }

    return errors;
  };

  const handleAddShopSubmit = async (e) => {
    e.preventDefault();
    const errors = validateAddShop(addShopFormValues);
    setAddShopFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      const allShopsRes = await getAllBarberShop();
      if (allShopsRes.data.statusCode === 200 && checkDuplicateShop(allShopsRes.data.shopList, addShopFormValues)) {
        setAddShopFormErrors((prev) => ({ ...prev, name: DUPLICATE_SHOP_MESSAGE, stateProvinceId: DUPLICATE_SHOP_HELPER_MESSAGE }));
        return;
      }
      setAddShopLoader(true);
      createShop(buildShopCreatePayload(addShopFormValues)).then((res) => {
        if (res.data.statusCode === 200) {
          getAllBarberShop().then((allShopsRes) => {
            setAddShopLoader(false);
            let newShopId = "";
            let newShopName = addShopFormValues.name;
            if (allShopsRes.data.statusCode === 200) {
              const newShop = allShopsRes.data.shopList.find((shop) =>
                shop.name && shop.name.trim().toLowerCase() === addShopFormValues.name.trim().toLowerCase() &&
                shop.address1 && shop.address1.trim().toLowerCase() === addShopFormValues.address1.trim().toLowerCase()
              );
              if (newShop) {
                newShopId = newShop.shopId;
                newShopName = newShop.name;
              }
            }
            navigate(parentPath, {
              state: {
                clientId,
                firstName: professionalFirstName,
                lastName: professionalLastName,
                phoneNumber: professionalPhoneNumber,
                newShopId,
                newShopName,
              },
            });
            Swal.fire({
              position: "center",
              icon: "success",
              title: "Business added successfully!",
              showConfirmButton: false,
              timer: 3000,
            });
          });
        } else if (res.data.statusCode === 400) {
          // Backend's own duplicate-business check (Name + Address1 + State) - same rule as
          // our client-side pre-check above, kept as a safety net. Uses our own wording
          // (not res.data.statusMessage) so both layers show identical text to the user.
          setAddShopLoader(false);
          setAddShopFormErrors((prev) => ({ ...prev, name: DUPLICATE_SHOP_MESSAGE, stateProvinceId: DUPLICATE_SHOP_HELPER_MESSAGE }));
        } else {
          setAddShopLoader(false);
          Swal.fire({ icon: "error", title: "Oops...", text: res.data.statusMessage });
        }
      }).catch(() => setAddShopLoader(false));
    }
  };

  const handleCancel = () => {
    navigate(parentPath, {
      state: {
        clientId,
        firstName: professionalFirstName,
        lastName: professionalLastName,
        phoneNumber: professionalPhoneNumber,
      },
    });
  };

  return (
    <section className="login-bg register-bg">
      <Container>
        <Row className="justify-content-md-center">
          <Col md={6}>
            <div className="text-center">
              <img src="/images/logo.png" />
            </div>
            <h1 className="text-center">
              Add <span>Shop</span>
            </h1>
            <Form onSubmit={handleAddShopSubmit}>
              <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>Business Name <span className="error">*</span></Form.Label>
                  <Form.Control
                    onKeyDown={BlockSQLInjection}
                    type="text"
                    name="name"
                    value={addShopFormValues.name}
                    onChange={handleAddShopFormChange}
                    placeholder="Enter business name"
                  />
                  {addShopFormErrors.name && <span className="error">{addShopFormErrors.name}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>Are you the <span style={{ whiteSpace: "nowrap", color: "inherit" }}>owner? <span className="error">*</span></span></Form.Label>
                  <Form.Select name="isOwnership" value={addShopFormValues.isOwnership} onChange={handleAddShopFormChange}>
                    <option value="">-- Select --</option>
                    <option value="Y">Yes</option>
                    <option value="N">No</option>
                  </Form.Select>
                  {addShopFormErrors.isOwnership && <span className="error">{addShopFormErrors.isOwnership}</span>}
                </Form.Group>
              </Row>

              {addShopFormValues.isOwnership === "Y" && (
                <>
                  <Row className="mb-2">
                    <Form.Group as={Col} className="mb-2">
                      <Form.Label>Owner First Name <span className="error">*</span></Form.Label>
                      <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="managerFirstName"
                        value={addShopFormValues.managerFirstName}
                        onChange={handleAddShopFormChange}
                        placeholder="Enter first name"
                        disabled={addShopFormValues.isOwnership === "Y"}
                        style={addShopFormValues.isOwnership === "Y" ? { backgroundColor: "#2a2a2a", color: "#9a9a9a", cursor: "not-allowed" } : undefined}
                      />
                      {addShopFormErrors.managerFirstName && <span className="error">{addShopFormErrors.managerFirstName}</span>}
                    </Form.Group>
                    <Form.Group as={Col} className="mb-2">
                      <Form.Label>Owner Last Name <span className="error">*</span></Form.Label>
                      <Form.Control
                        onKeyDown={BlockSQLInjection}
                        type="text"
                        name="managerLastName"
                        value={addShopFormValues.managerLastName}
                        onChange={handleAddShopFormChange}
                        placeholder="Enter last name"
                        disabled={addShopFormValues.isOwnership === "Y"}
                        style={addShopFormValues.isOwnership === "Y" ? { backgroundColor: "#2a2a2a", color: "#9a9a9a", cursor: "not-allowed" } : undefined}
                      />
                      {addShopFormErrors.managerLastName && <span className="error">{addShopFormErrors.managerLastName}</span>}
                    </Form.Group>
                  </Row>
                  <Row className="mb-2">
                    <Form.Group as={Col} className="mb-2">
                      <Form.Label>Owner Phone Number <span className="error">*</span></Form.Label>
                      <Form.Control
                        onKeyDown={handleAddShopPhoneKeyDown}
                        type="text"
                        name="managerPhoneNumber"
                        value={addShopFormValues.managerPhoneNumber}
                        onChange={handleAddShopFormChange}
                        placeholder="(XXX) XXX-XXXX"
                        maxLength={14}
                        disabled={addShopFormValues.isOwnership === "Y"}
                        style={addShopFormValues.isOwnership === "Y" ? { backgroundColor: "#2a2a2a", color: "#9a9a9a", cursor: "not-allowed" } : undefined}
                      />
                      {addShopFormErrors.managerPhoneNumber && <span className="error">{addShopFormErrors.managerPhoneNumber}</span>}
                    </Form.Group>
                    <Col></Col>
                  </Row>
                </>
              )}

              <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>Address 1 <span className="error">*</span></Form.Label>
                  <Form.Control
                    onKeyDown={BlockSQLInjection}
                    type="text"
                    name="address1"
                    value={addShopFormValues.address1}
                    onChange={handleAddShopFormChange}
                    placeholder="Enter address 1"
                  />
                  {addShopFormErrors.address1 && <span className="error">{addShopFormErrors.address1}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>Address 2</Form.Label>
                  <Form.Control
                    onKeyDown={BlockSQLInjection}
                    type="text"
                    name="address2"
                    value={addShopFormValues.address2}
                    onChange={handleAddShopFormChange}
                    placeholder="Enter address 2"
                  />
                </Form.Group>
              </Row>

              <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>City <span className="error">*</span></Form.Label>
                  <Form.Control
                    onKeyDown={BlockSQLInjection}
                    type="text"
                    name="city"
                    value={addShopFormValues.city}
                    onChange={handleAddShopFormChange}
                    placeholder="Enter city"
                  />
                  {addShopFormErrors.city && <span className="error">{addShopFormErrors.city}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2 select-search-drp">
                  <Form.Label>State <span className="error">*</span></Form.Label>
                  <Select
                    className="selectSearch"
                    options={stateProvinceOption}
                    name="stateProvinceId"
                    onChange={(selected) => setAddShopFormValues((prev) => ({ ...prev, stateProvinceId: selected ? selected.value : "" }))}
                    value={stateProvinceOption.find((s) => s.value === addShopFormValues.stateProvinceId) || null}
                    placeholder="Select state"
                  />
                  {addShopFormErrors.stateProvinceId && <span className="error">{addShopFormErrors.stateProvinceId}</span>}
                </Form.Group>
              </Row>

              <Row className="mb-2">
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>Zipcode <span className="error">*</span></Form.Label>
                  <Form.Control
                    onKeyDown={BlockSQLInjection}
                    type="text"
                    name="zipCode"
                    value={addShopFormValues.zipCode}
                    onChange={handleAddShopFormChange}
                    placeholder="Enter zipcode"
                    maxLength={5}
                  />
                  {addShopFormErrors.zipCode && <span className="error">{addShopFormErrors.zipCode}</span>}
                </Form.Group>
                <Form.Group as={Col} className="mb-2">
                  <Form.Label>Phone Number <span className="error">*</span></Form.Label>
                  <Form.Control
                    onKeyDown={handleAddShopPhoneKeyDown}
                    type="text"
                    name="phoneNumber"
                    value={addShopFormValues.phoneNumber}
                    onChange={handleAddShopFormChange}
                    placeholder="(XXX) XXX-XXXX"
                    maxLength={14}
                  />
                  {addShopFormErrors.phoneNumber && <span className="error">{addShopFormErrors.phoneNumber}</span>}
                </Form.Group>
              </Row>

              <Stack direction="horizontal" gap={2} className="justify-content-start align-items-stretch">
                <Button variant="secondary" className="wizardActionBtn" onClick={handleCancel}>Cancel</Button>
                <Button variant="primary" className="wizardActionBtn" type="submit" disabled={addShopLoader}>
                  {addShopLoader ? "Saving..." : "Save"}
                </Button>
              </Stack>
            </Form>
          </Col>
        </Row>
      </Container>
    </section>
  );
}
